/**
 * Material & Lieferanten am Projekt (Sektion auf der Projektseite).
 *
 * Benjamin, 25.09.2026: „Wir führen in einem Projekt Materialien (die in einem
 * Angebot vorkommen). Zu jedem Material gibt es einen Standardlieferanten —
 * der aber auch auf einen anderen geändert werden kann." Und: „Ich kann die
 * Lieferanten direkt aus dem Projekt heraus anschreiben."
 *
 * - „Aus Angebot übernehmen": Positionen der Angebote, je mit Materialvorschlag
 *   aus dem Stamm; übernommen wird nur, was angehakt ist.
 * - Darunter nach Lieferant gruppiert, mit Anrufen, Mail und „Anfrage".
 * - „Anfrage schreiben" (Migration 175): eine Mail je Lieferant, fertig
 *   geschrieben, mehrere Lieferanten je Material zum Preisvergleich
 *   (`AnfrageDialog`). Je Material steht danach sein Stand (`AnfrageStand`),
 *   ausstehende Antworten melden sich oben (Wiedervorlage).
 * - „+ Lieferant" für Lieferanten ohne Material (Container, Maschinen).
 * - Drei Ansichten (Benjamin, 02.10.2026: „übersichtlicher, was man dort
 *   sieht"): nach Lieferant (gruppiert, wie bisher), Material (eine Liste,
 *   der Lieferant steht in der Zeile) und Lieferscheine (was wann kam).
 *   Die Knopfleiste oben gilt für alle drei.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { fehlerText } from "@vimaana/ui";

import {
  useProjektMaterial, useProjektMaterialAendern,
  type ProjektLieferantGruppe, type ProjektMaterialZeile,
} from "../../hooks/useLieferanten";
import { useProjektBelege } from "../../hooks/usePlanung";
import { telLink } from "../../lib/notizen";
import type { Dokument } from "../DokumentLightbox";
import LieferantBlatt from "../lieferanten/LieferantBlatt";
import MaterialWahl from "../lieferanten/MaterialWahl";
import LieferantSuche from "../lieferanten/LieferantSuche";
import AnfrageDialog, { type AnfrageStart } from "./AnfrageDialog";
import { Wiedervorlagen, standVon } from "./AnfrageStand";
import AngebotUebernahme from "./AngebotUebernahme";
import LieferscheinListe from "./LieferscheinListe";
import MaterialZeile from "./MaterialZeile";
import PostSchreiben, { type SchreibZiel } from "./PostSchreiben";
import "../../styles/lieferanten.css";

type Ansicht = "lieferanten" | "material" | "lieferscheine";

export default function ProjektMaterial({ projektId, onZeigen }: { projektId: string; onZeigen: (d: Dokument) => void }) {
  const daten = useProjektMaterial(projektId);
  const belege = useProjektBelege(projektId);
  const [ansicht, setAnsicht] = useState<Ansicht>("lieferanten");
  const a = useProjektMaterialAendern(projektId);
  const [uebernahme, setUebernahme] = useState(false);
  const [materialDazu, setMaterialDazu] = useState(false);
  const [lieferantDazu, setLieferantDazu] = useState(false);
  const [blatt, setBlatt] = useState<string | null>(null);
  const [schreiben, setSchreiben] = useState<ProjektLieferantGruppe | null>(null);
  const [anfrage, setAnfrage] = useState<AnfrageStart | null>(null);
  const [stand, setStand] = useState<string | null>(null);
  // Anderes Projekt, selbe Seite: ein offenes Lieferantenblatt gehört nicht mit.
  useEffect(() => { setBlatt(null); setSchreiben(null); setAnfrage(null); setStand(null); }, [projektId]);

  const d = daten.data;
  if (daten.isPending) return <p className="lf-leer">Lade …</p>;
  if (daten.isError || !d) return <p className="lf-fehler">{fehlerText(daten.error)}</p>;

  const zeile = (id: string) => d.materialien.find((m) => m.id === id);
  const lieferscheine = (belege.data ?? []).filter((b) => b.beleg_typ === "lieferschein");
  const ohne = d.ohne_lieferant.map(zeile).filter(Boolean) as ProjektMaterialZeile[];
  const fehler = a.aendern.error || a.entfernen.error || a.lieferantDazu.error || a.lieferantWeg.error || a.uebernehmen.error;
  // Was sich anfragen lässt: hat einen Lieferanten (oder jemanden, der es führt) und steht noch auf „nicht angefragt".
  const anzufragen = d.materialien.filter((m) => (m.lieferant_id || m.anbieter.length) && standVon(m).art === "offen").length;
  const standUmschalten = (pmId: string) => setStand((s) => (s === pmId ? null : pmId));

  return (
    <div className="lf-projekt">
      <div className="lf-projekt-leiste">
        <button type="button" className={"lf-knopf" + (d.angebot_offen > 0 || d.materialien.length === 0 ? " is-primaer" : "")} onClick={() => setUebernahme(true)}>
          Aus Angebot übernehmen{d.angebot_offen > 0 && <span className="lf-zahl">{d.angebot_offen}</span>}
        </button>
        {d.materialien.length > 0 && (
          <button type="button" className={"lf-knopf" + (anzufragen > 0 && d.angebot_offen === 0 ? " is-primaer" : "")} onClick={() => setAnfrage({})}
            title="Eine Mail je Lieferant — mit Material, Mengen, Lieferort und Termin">
            ✉ Anfrage schreiben{anzufragen > 0 && <span className="lf-zahl">{anzufragen}</span>}
          </button>
        )}
        <button type="button" className="lf-knopf" onClick={() => { setMaterialDazu((v) => !v); setLieferantDazu(false); }}>＋ Material</button>
        <button type="button" className="lf-knopf" onClick={() => { setLieferantDazu((v) => !v); setMaterialDazu(false); }}>＋ Lieferant</button>
      </div>
      {fehler && <div className="lf-fehler">{fehlerText(fehler)}</div>}
      {materialDazu && (
        <MaterialWahl
          vorhanden={d.materialien.map((m) => m.material_id)}
          onAbbruch={() => setMaterialDazu(false)}
          onWahl={(m) => a.uebernehmen.mutate([{ material_id: m.id, einheit: m.einheit }], { onSuccess: () => setMaterialDazu(false) })}
        />
      )}
      {lieferantDazu && (
        <LieferantSuche
          ausser={d.gruppen.map((g) => g.lieferant_id)}
          onAbbruch={() => setLieferantDazu(false)}
          onWahl={(id) => a.lieferantDazu.mutate(id, { onSuccess: () => setLieferantDazu(false) })}
        />
      )}

      <Wiedervorlagen d={d} projektId={projektId} onStand={(pmId) => setStand(pmId)} />

      <div className="vg-proj-tabs lf-ansichten" role="tablist" aria-label="Ansicht">
        {([
          ["lieferanten", "Nach Lieferant", d.gruppen.length + (ohne.length > 0 ? 1 : 0)],
          ["material", "Material", d.materialien.length],
          ["lieferscheine", "Lieferscheine", lieferscheine.length],
        ] as const).map(([id, label, anzahl]) => (
          <button key={id} type="button" role="tab" aria-selected={ansicht === id}
            className={`vg-proj-tab${ansicht === id ? " is-active" : ""}`} onClick={() => setAnsicht(id)}>
            {label}<span className="vg-proj-tab-count">{anzahl}</span>
          </button>
        ))}
      </div>

      {ansicht === "material" && (d.materialien.length === 0 ? (
        <p className="lf-leer">Noch kein Material am Projekt.</p>
      ) : (
        <ul className="lf-liste lf-materialansicht">
          {d.materialien.map((m) => (
            <MaterialZeile key={m.id} m={m} projektId={projektId} standOffen={stand === m.id} mitLieferant
              onStand={() => standUmschalten(m.id)} onAnfrage={() => setAnfrage({ pmIds: [m.id] })} />
          ))}
        </ul>
      ))}

      {ansicht === "lieferscheine" && (belege.isPending ? (
        <p className="lf-leer">Lade Lieferscheine …</p>
      ) : lieferscheine.length === 0 ? (
        <p className="lf-leer">Noch kein Lieferschein zu diesem Projekt. In ScanFlow als „Lieferschein“ erfassen und das Projekt wählen.</p>
      ) : (
        <LieferscheinListe lieferscheine={lieferscheine} projektId={projektId} onZeigen={onZeigen} />
      ))}

      {ansicht === "lieferanten" && <>

      {d.materialien.length === 0 && d.gruppen.length === 0 && (
        <p className="lf-leer">
          Noch kein Material am Projekt. „Aus Angebot übernehmen“ holt es aus den Positionen des Angebots —
          der Lieferant kommt dann vom Material (★ auf der Lieferantenseite).
        </p>
      )}

      {d.gruppen.map((g) => {
        const tel = g.kontakte.find((k) => k.telefon)?.telefon || "";
        const mails = g.kontakte.filter((k) => k.email);
        return (
          <section key={g.lieferant_id} className="lf-gruppe">
            <header className="lf-gruppe-kopf">
              <button type="button" className="lf-gruppe-name" onClick={() => setBlatt(g.lieferant_id)} title="Lieferantenseite öffnen">
                {g.name}
                {g.adresse && <small>{g.adresse}</small>}
                {g.aus_beleg && !g.von_hand && g.material_ids.length === 0 && <small>aus Rechnungen und Belegen</small>}
              </button>
              <div className="lf-gruppe-tat">
                {tel && <a className="lf-knopf" href={telLink(tel)} title={`${tel} anrufen`}>✆ Anrufen</a>}
                {mails.length > 0 && <button type="button" className="lf-knopf" onClick={() => setSchreiben(g)}>✎ Mail</button>}
                {g.material_ids.length > 0 && (
                  <button type="button" className={"lf-knopf" + (g.material_ids.some((id) => { const m = zeile(id); return m && standVon(m).art === "offen"; }) ? " is-primaer" : "")}
                    onClick={() => setAnfrage({ lieferantId: g.lieferant_id })}
                    title={g.anfrage_an?.email ? `Anfrage an ${g.anfrage_an.email}` : (g.anfrage_an?.hinweis || "Keine Adresse für Anfragen")}>
                    ✉ Anfrage{!g.anfrage_an?.email && " !"}
                  </button>
                )}
                {mails.length === 0 && (
                  <button type="button" className="lf-knopf" onClick={() => setBlatt(g.lieferant_id)} title="Keine Mailadresse hinterlegt">Kontakt ergänzen</button>
                )}
                {g.von_hand && g.material_ids.length === 0 && (
                  <button type="button" className="lf-knopf" disabled={a.lieferantWeg.isPending} onClick={() => a.lieferantWeg.mutate(g.lieferant_id)}
                    title="Nur die Zuordnung lösen — Mails und Belege bleiben">Lösen</button>
                )}
              </div>
            </header>
            {g.material_ids.length > 0 && (
              <ul className="lf-liste">
                {g.material_ids.map((id) => zeile(id)).filter(Boolean).map((m) => (
                  <MaterialZeile key={m!.id} m={m!} projektId={projektId} standOffen={stand === m!.id}
                    onStand={() => standUmschalten(m!.id)} onAnfrage={() => setAnfrage({ pmIds: [m!.id] })} />
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {ohne.length > 0 && (
        <section className="lf-gruppe ist-ohne">
          <header className="lf-gruppe-kopf">
            <div className="lf-gruppe-name">Ohne Lieferant<small>Für diese Materialien ist noch kein Standardlieferant hinterlegt.</small></div>
          </header>
          <ul className="lf-liste">
            {ohne.map((m) => (
              <MaterialZeile key={m.id} m={m} projektId={projektId} standOffen={stand === m.id}
                onStand={() => standUmschalten(m.id)} onAnfrage={() => setAnfrage({ pmIds: [m.id] })} />
            ))}
          </ul>
        </section>
      )}

      </>}

      {uebernahme && <AngebotUebernahme projektId={projektId} onClose={() => setUebernahme(false)} onAnfragen={(pmIds) => setAnfrage({ pmIds })} />}
      {blatt && <LieferantBlatt lieferantId={blatt} onClose={() => setBlatt(null)} />}
      {anfrage && <AnfrageDialog projektId={projektId} start={anfrage} onClose={() => setAnfrage(null)} />}
      {schreiben && createPortal(
        <PostSchreiben
          onClose={() => setSchreiben(null)}
          projektId={projektId}
          lieferantId={schreiben.lieferant_id}
          ziele={schreiben.kontakte.filter((k) => k.email).map<SchreibZiel>((k) => ({ email: k.email, wer: [k.name, k.rolle].filter(Boolean).join(" · ") || schreiben.name }))}
          an={schreiben.anfrage_an?.email || schreiben.kontakte.find((k) => k.email)?.email || ""}
          titel={schreiben.name}
        />,
        document.body,
      )}
    </div>
  );
}
